import { logger as baseLogger } from "matrix-js-sdk/src/logger";
import { type ReactNode } from "react";
import {
    AccessTokens,
    // AccessTokens,
    // IRefreshTokenResponse,
    type MatrixClient,
    // OidcTokenRefresher,
    decodeBase64,
} from "matrix-js-sdk/src/matrix";
import { type QueryDict } from "matrix-js-sdk/src/utils";

import { type IMatrixClientCreds, MatrixClientPeg, type MatrixClientPegAssignOpts } from "./MatrixClientPeg";
import EventIndexPeg from "./indexing/EventIndexPeg";
import createMatrixClient from "./utils/createMatrixClient";
import Notifier from "./Notifier";
import UserActivity from "./UserActivity";
import Presence from "./Presence";
import dis from "./dispatcher/dispatcher";
import DMRoomMap from "./utils/DMRoomMap";
import Modal from "./Modal";
import ActiveWidgetStore from "./stores/ActiveWidgetStore";
import PlatformPeg from "./PlatformPeg";
import * as StorageManager from "./utils/StorageManager";
import SettingsStore from "./settings/SettingsStore";
// import { SettingLevel } from "./settings/SettingLevel";
import ToastStore from "./stores/ToastStore";
import { IntegrationManagers } from "./integrations/IntegrationManagers";
// import { Mjolnir } from "./mjolnir/Mjolnir";
import DeviceListener from "./DeviceListener";
// import { Jitsi } from "./widgets/Jitsi";
// import ThreepidInviteStore from "./stores/ThreepidInviteStore";
// import { PosthogAnalytics } from "./PosthogAnalytics";
// import LegacyCallHandler from "./LegacyCallHandler";
// import LifecycleCustomisations from "./customisations/Lifecycle";
import ErrorDialog from "./components/views/dialogs/ErrorDialog";
import { _t } from "./languageHandler";
import SessionRestoreErrorDialog from "./components/views/dialogs/SessionRestoreErrorDialog";
import StorageEvictedDialog from "./components/views/dialogs/StorageEvictedDialog";
import { setSentryUser } from "./sentry";
import SdkConfig from "./SdkConfig";
import { DialogOpener } from "./utils/DialogOpener";
import { Action } from "./dispatcher/actions";
import { type OverwriteLoginPayload } from "./dispatcher/payloads/OverwriteLoginPayload";
import { SdkContextClass } from "./contexts/SDKContext";
import { getOidcErrorMessage } from "./utils/oidc/error";
// import { type OidcClientStore } from "./stores/oidc/OidcClientStore";
// import {
//     getStoredOidcClientId, getStoredOidcIdTokenClaims, getStoredOidcTokenIssuer,
//     persistOidcAuthenticatedSettings
// } from "./utils/oidc/persistOidcSettings";
import { checkBrowserSupport } from "./SupportedBrowser";
import { DthHttpClient } from "./dth/clientHttp/DthHttpClient";
import { convertIDthUserSessionToMatrixClientCreds } from "./dth/clientHttp/utils/utils";
import { DthSessionStorage } from "./dth/clientHttp/DthSessionStorage";
import BasePlatform from "./BasePlatform";
import {
    API_AUTH_PROVIDER_KEY, API_TELEMETRY_TOKEN_KEY, API_USER_ID_KEY,
    AUTH_PROVIDER_ENTRA_ID, AUTH_PROVIDER_TYPE, DELETE_RECOVERY_KEY_ON_ACCOUNT_CHANGE, getAuthProviderFromString,
    HAS_MX_ACCESS_TOKEN_STORAGE_KEY
} from "./dth/constants";
import { IDthPrivateConfiguration, IDthUser } from "./dth/clientHttp/models/httpClientModels";
import { requestFirebaseNotificationPermission } from "./dth/firebase/firebase";
import { useCallStore } from "./stores/call/useCallStore";
import { LicenseManager } from "./dth/license/LicenseManager";
import LicenseError from "./dth/clientHttp/errors/LicenseError";
import { completeCustomOidcLogin } from "./dth/sso/ssoUtils";
import HttpResponseError from "./dth/clientHttp/errors/HttpResponseError";
import _ from "lodash";
import CustomError from "./dth/clientHttp/errors/CustomError";
import { promiseWithTimeout } from "./dth/utils";
import TimeoutError from "./dth/clientHttp/errors/TimeoutError";
// import { TokenRefresher } from "./utils/oidc/TokenRefresher";
import { ContactStateManager } from "./customisations/ContactStateManager";
import DthValidationTokenManager from "./dth/clientHttp/DthValidationTokenManager";
import { DthRecoveryKeyManager } from "./dth/clientHttp/DthRecoveryKeyManager";
import { DthLoginPasswordCache } from "./dth/clientHttp/DthLoginPasswordCache";

const logger = baseLogger.getChild("Lifecycle");

dis.register((payload) => {
    if (payload.action === Action.TriggerLogout) {
        logger.warn("[Action.TriggerLogout] Triggering logout from dispatcher action", payload);
        // noinspection JSIgnoredPromiseFromCall - we don't care if it fails
        onLoggedOut();
    } else if (payload.action === Action.OverwriteLogin) {
        logger.warn("[Action.OverwriteLogin] Overwriting login from dispatcher action", payload);
        const typed = <OverwriteLoginPayload>payload;
        // Stop the current client before overwriting the login.
        // If not done it might be impossible to clear the storage, as the
        // rust crypto backend might be holding an open connection to the indexeddb store.
        // We also use the `unsetClient` flag to false, because at this point we are
        // already in the logged in flows of the `MatrixChat` component, and it will
        // always expect to have a client (calls to `MatrixClientPeg.safeGet()`).
        // If we unset the client and the component is updated,  the render will fail and unmount everything.
        // (The module dialog closes and fires a `aria_unhide_main_app` that will trigger a re-render)
        stopMatrixClient(false);
        doSetLoggedIn(typed.credentials, null, true, true).catch((e) => {
            // XXX we might want to fire a new event here to let the app know that the login failed ?
            // The module api could use it to display a message to the user.
            logger.warn("Failed to overwrite login", e);
        });
    }
});

/**
 * This is set to true by {@link #onSessionLockStolen}.
 *
 * It is used in various of the async functions to prevent races where we initialise a client after the lock is stolen.
 */
let sessionLockStolen = false;

// this is exposed solely for unit tests.
export function setSessionLockNotStolen(): void {
    sessionLockStolen = false;
}

/**
 * Handle the session lock being stolen. Stops any active Matrix Client, and aborts any ongoing client initialisation.
 */
export async function onSessionLockStolen(): Promise<void> {
    sessionLockStolen = true;
    stopMatrixClient();
}

/**
 * Check if we still hold the session lock.
 *
 * If not, raises a {@link SessionLockStolenError}.
 */
function checkSessionLock(): void {
    if (sessionLockStolen) {
        throw new SessionLockStolenError("session lock has been released");
    }
}

/** Error type raised by various functions in the Lifecycle workflow if session lock is stolen during execution */
class SessionLockStolenError extends Error { }

interface ILoadSessionOpts {
    defaultDeviceDisplayName?: string;
    fragmentQueryParams?: QueryDict;
    abortSignal?: AbortSignal;
}

/**
 * Called at startup, to attempt to build a logged-in Matrix session. It tries
 * a number of things:
 *
 * 1. if we have a guest access token in the fragment query params, it uses
 *    that.
 * 2. if an access token is stored in local storage (from a previous session),
 *    it uses that.
 * 3. it attempts to auto-register as a guest user.
 *
 * If any of steps 1-4 are successful, it will call {_doSetLoggedIn}, which in
 * turn will raise on_logged_in and will_start_client events.
 *
 * @param {object} [opts]
 * @param {object} [opts.fragmentQueryParams]: string->string map of the
 *     query-parameters extracted from the #-fragment of the starting URI.
 * @param {string} [opts.defaultDeviceDisplayName]: Default display name to use
 *     when registering as a guest.
 * @returns {Promise} a promise which resolves when the above process completes.
 *     Resolves to `true` if we ended up starting a session, or `false` if we
 *     failed.
 */
export async function loadSession(opts: ILoadSessionOpts = {}): Promise<boolean> {
    logger.debug("loadSession started with opts:", opts);
    try {
        const success = await restoreSessionFromStorage();
        if (success) {
            return true;
        }

        if (sessionLockStolen) {
            return false;
        }

        // se la restore non avviene, prima di tornare alla Welcome deve svuotare la local storage
        await clearStorage();

        // fall back to welcome screen
        return false;
    } catch (e) {
        // We may be aborted e.g. because our token expired, so don't show an error here
        if (opts.abortSignal?.aborted) {
            return false;
        }

        if (e instanceof LicenseError) {
            // If we're aborting login because of a license error, we don't
            // need to show the general failure dialog. Instead, just go back to welcome.
            return false;
        }

        if (e instanceof AbortLoginAndRebuildStorage) {
            // If we're aborting login because of a storage inconsistency, we don't
            // need to show the general failure dialog. Instead, just go back to welcome.
            return false;
        }


        // likewise, if the session lock has been stolen while we've been trying to start
        if (sessionLockStolen) {
            return false;
        }

        return handleLoadSessionFailure(e, opts);
    }
}

/**
 * Gets the user ID of the persisted session, if one exists. This does not validate
 * that the user's credentials still work, just that they exist and that a user ID
 * is associated with them. The session is not loaded.
 * @returns {[string, boolean]} The persisted session's owner and whether the stored
 *     session is for a guest user, if an owner exists. If there is no stored session,
 *     return [null, null].
 */
export async function getStoredSessionOwner(): Promise<[string, boolean] | [null, null]> {
    const { homeserverUrl, userId, guest, accessToken, apiAccessToken } = await DthSessionStorage.getCredentials();
    const isGuest = !!guest;
    return homeserverUrl && userId && accessToken && apiAccessToken ? [userId, !!isGuest] : [null, null];
}

/**
 * If query string includes OIDC authorization code flow parameters attempt to login using oidc flow
 * Else, we may be returning from SSO - attempt token login
 *
 * @param {Object} queryParams    string->string map of the
 *     query-parameters extracted from the real query-string of the starting
 *     URI.
 *
 * @returns {Promise} promise which resolves to true if we completed the delegated auth login
 *      else false
 */
export async function attemptDelegatedCustomAuthLogin(
    queryParams: QueryDict
): Promise<IMatrixClientCreds | null> {
    if (queryParams.code && queryParams.state) {
        logger.debug("We have OIDC params - attempting OIDC login");
        return attemptCustomOidcNativeLogin();
    }

    return null;
}

/**
 * Attempt to login by completing OIDC authorization code flow
 * @returns Promise that resolves to true when login succceeded, else false
 */
async function attemptCustomOidcNativeLogin(): Promise<IMatrixClientCreds | null> {
    try {
        const response = await completeCustomOidcLogin();

        const dthApiClient = DthHttpClient.getInstance();

        const userSession = await dthApiClient.oauthLogin({
            provider: response.provider,
            accessToken: response.accessToken,
            idToken: response.idToken
        });

        // const matrixUserId = userSession.session.matrixUserId;
        // const issuer = response.issuer;
        // const idToken = response.idToken;

        const credentials: IMatrixClientCreds = convertIDthUserSessionToMatrixClientCreds(
            response.homeserverUrl,
            userSession,
            response.identityServerUrl
        )

        logger.debug("Logged in via OIDC native flow");

        // nuove credenziali ottenute dal matrix login
        // const matrixCredentials = await matrixLogin(response.homeserverUrl, userSession);

        await onSuccessfulDelegatedAuthLogin(credentials, userSession.user);

        // this needs to happen after success handler which clears storages
        // persistOidcAuthenticatedSettings(matrixUserId, issuer, idToken);
        return credentials;
    } catch (error) {
        logger.error("Failed to login via OIDC", error);

        onFailedDelegatedAuthLogin(getOidcErrorMessage(error as Error));
        return null;
    }
}

/**
 * Load the pickle key inside the credentials or create it if it does not exist for this device.
 *
 * @param credentials Holds the device to load/store the pickle key
 *
 * @returns {Promise} promise which resolves to the loaded or generated pickle key or undefined if
 *    none was loaded nor generated
 */
async function loadOrCreatePickleKey(userId: string, deviceId?: string): Promise<string | undefined> {
    const basePlatform: BasePlatform | null = PlatformPeg.get();

    // Verifica se basePlatform è disponibile
    if (!basePlatform) {
        logger.error("Base platform is not available.");
        return undefined;
    }

    // Carica la chiave pickle esistente
    let pickleKey = await basePlatform.getPickleKey(userId, deviceId ?? "") ?? undefined;

    if (pickleKey) {
        // Se la chiave pickle esiste già, logga e restituisci
        console.debug(`[Lifecycle] Pickle key already exists for ${userId}|${deviceId}. No new key created.`);
    } else {
        // Se la chiave pickle non esiste, creala
        if (userId && deviceId) {
            pickleKey = await basePlatform.createPickleKey(userId, deviceId) ?? undefined;
            if (pickleKey) {
                console.debug(`[Lifecycle] Created pickle key for ${userId}|${deviceId}`);
            } else {
                logger.warn("Pickle key not created.");
            }
        } else {
            logger.warn("Invalid credentials: userId or deviceId missing, pickle key not created.");
        }
    }

    return pickleKey;
}

/**
 * Called after a successful token login or OIDC authorization.
 * Clear storage then save new credentials in storage
 * @param credentials as returned from login
 */
async function onSuccessfulDelegatedAuthLogin(credentials: IMatrixClientCreds, user: IDthUser): Promise<void> {
    logger.debug("onSuccessfulDelegatedAuthLogin: Clearing storage and saving new credentials");

    // È IMPORTANTE svuotare la storage prima di salvare le nuove credenziali, 
    // in modo da evitare che eventuali dati residui di una sessione precedente (es. token scaduti) 
    // interferiscano con la nuova sessione.
    await clearStorage();

    // Vengono salvate le nuove credenziali, che includono il nuovo access token ottenuto dal flusso di autenticazione delegata (es. OIDC)
    // È IMPORTANTE salvare le info utente per non perdere per esempio il telemetry token che può essere ottenuto solo dal login e non dalle info utente
    // SSO does not go through setLoggedIn so we need to load/create the pickle key here too
    credentials.pickleKey = await loadOrCreatePickleKey(credentials.userId, credentials.deviceId);
    await DthSessionStorage.saveAllSessionInfo(credentials, user, AUTH_PROVIDER_ENTRA_ID);

    // remember that we just logged in
    sessionStorage.setItem("mx_fresh_login", String(true));
}

type TryAgainFunction = () => void;

/**
 * Display a friendly error to the user when token login or OIDC authorization fails
 * @param description error description
 * @param tryAgain OPTIONAL function to call on try again button from error dialog
 */
function onFailedDelegatedAuthLogin(description: string | ReactNode, tryAgain?: TryAgainFunction): void {
    const { finished } = Modal.createDialog(ErrorDialog, {
        title: _t("auth.oidc.error_title"),
        description,
        button: _t("action.try_again"),
    });

    finished.then(([shouldTryAgain]) => {
        // if we have a tryAgain callback, call it the primary 'try again' button was clicked in the dialog
        if (shouldTryAgain) tryAgain?.();
    });
}

async function abortLogin(): Promise<void> {
    const signOut = await showStorageEvictedDialog();
    logger.debug("abortLogin: showing StorageEvictedDialog, user chose to sign out:", signOut);
    if (signOut) {
        await clearStorage();
        // This error feels a bit clunky, but we want to make sure we don't go any
        // further and instead head back to sign in.
        throw new AbortLoginAndRebuildStorage("Aborting login in progress because of storage inconsistency");
    }
}

/** Attempt to restore the session from localStorage or indexeddb.
 *
 * @returns true if a session was found; false if no existing session was found.
 *
 * N.B. Lifecycle.js should not maintain any further localStorage state, we
 *      are moving towards using SessionStore to keep track of state related
 *      to the current session (which is typically backed by localStorage).
 *
 *      The plan is to gradually move the localStorage access done here into
 *      SessionStore to avoid bugs where the view becomes out-of-sync with
 *      localStorage (e.g. isGuest etc.)
 */
export async function restoreSessionFromStorage(opts?: { ignoreGuest?: boolean }): Promise<boolean> {
    logger.debug("Attempting to restore session from storage");
    const ignoreGuest = opts?.ignoreGuest;

    if (!localStorage) {
        return false;
    }

    let matrixClientCreds: IMatrixClientCreds | null = null;
    // Controlla se l'api access token è da aggiornare prima di provare ad effettuare il restore della sessione, 
    // in modo da evitare di inizializzare un client con un token scaduto.
    const apiClient = DthHttpClient.getInstance();
    // Se il token è da aggiornare, forza l'aggiornamento prima di loggare, se l'aggiornamento fallisce forza logout
    const isApiAccessTokenToUpdate = await apiClient.isApiAccessTokenToUpdate();
    if (isApiAccessTokenToUpdate) {
        logger.warn("API access token is due for refresh, updating before restoring session");
        // aggiorna l'api access token e ritorna le nuove credenziali del client Matrix, che includono il nuovo api access token
        // credenziali matrix contengono anche quelle di API, in questo caso le credenziali esclusivamente di Matrix non vengono impattate
        matrixClientCreds = await apiClient.updateApiAccessToken();
    } else {
        // altrimenti, se il token è ancora valido, preleva le credenziali del client Matrix dallo storage senza aggiornare il token
        matrixClientCreds = await DthSessionStorage.getCredentials();
    }

    //se l'access token è stato salvato ma non è possibile recuperarlo, vuol dire che il login deve essere annullato 
    const hasAccessToken = DthSessionStorage.getGenericItem(HAS_MX_ACCESS_TOKEN_STORAGE_KEY) === "true";
    if (hasAccessToken && !matrixClientCreds.accessToken) {
        logger.warn("restoreSessionFromStorage: storage indicates we should have an access token, but we do not. Displaying StorageEvictedDialog");
        await abortLogin();
    }

    if (matrixClientCreds.accessToken &&
        matrixClientCreds.apiAccessToken &&
        matrixClientCreds.userId &&
        matrixClientCreds.homeserverUrl) {

        if (ignoreGuest && matrixClientCreds.guest) {
            logger.debug("Ignoring stored guest account: " + matrixClientCreds.userId);
            return false;
        }

        const freshLogin = sessionStorage.getItem("mx_fresh_login") === "true";
        sessionStorage.removeItem("mx_fresh_login");

        console.debug(`[Lifecycle] Restoring session for ${matrixClientCreds.userId}`);
        await doSetLoggedIn(
            matrixClientCreds,
            getAuthProviderFromString(DthSessionStorage.getGenericItem(API_AUTH_PROVIDER_KEY)),
            false,
            freshLogin,
        );
        return true;
    }
    logger.debug("No previous session found.");
    return false;
}

async function handleLoadSessionFailure(e: unknown, loadSessionOpts?: ILoadSessionOpts): Promise<boolean> {
    logger.error("Unable to load session", e);

    const modal = Modal.createDialog(SessionRestoreErrorDialog, {
        error: e,
    });

    const [success] = await modal.finished;
    if (success) {
        // user clicked continue.
        await clearStorage();
        return false;
    }

    // try, try again
    return loadSession(loadSessionOpts);
}

/**
 * Transitions to a logged-in state using the given credentials.
 *
 * Starts the matrix client and all other react-sdk services that
 * listen for events while a session is logged in.
 *
 * Also stops the old MatrixClient and clears old credentials/etc out of
 * storage before starting the new client.
 *
 * @param {IMatrixClientCreds} credentials The credentials to use
 *
 * @returns {Promise} promise which resolves to the new MatrixClient once it has been started
 */
export async function setLoggedIn(credentials: IMatrixClientCreds, authProvider: AUTH_PROVIDER_TYPE | null): Promise<MatrixClient> {
    credentials.freshLogin = true;
    stopMatrixClient();
    return doSetLoggedIn(credentials, authProvider, true, true);
}

// /**
//  * Hydrates an existing session by using the credentials provided. This will
//  * not clear any local storage, unlike setLoggedIn().
//  *
//  * Stops the existing Matrix client (without clearing its data) and starts a
//  * new one in its place. This additionally starts all other react-sdk services
//  * which use the new Matrix client.
//  *
//  * If the credentials belong to a different user from the session already stored,
//  * the old session will be cleared automatically.
//  *
//  * @param {IMatrixClientCreds} credentials The credentials to use
//  *
//  * @returns {Promise} promise which resolves to the new MatrixClient once it has been started
//  */
// export async function hydrateSession(credentials: IMatrixClientCreds): Promise<MatrixClient> {
//     const oldUserId = MatrixClientPeg.safeGet().getUserId();
//     const oldDeviceId = MatrixClientPeg.safeGet().getDeviceId();

//     stopMatrixClient(); // unsets MatrixClientPeg.get()
//     localStorage.removeItem("mx_soft_logout");
//     setIsLoggingOut(false);

//     const overwrite = credentials.userId !== oldUserId || credentials.deviceId !== oldDeviceId;
//     if (overwrite) {
//         console.warn("[Lifecycle] Clearing all data: Old session belongs to a different user/session");
//     }

//     if (!credentials.pickleKey && credentials.deviceId !== undefined) {
//         console.debug("[Lifecycle] Lifecycle#hydrateSession: Pickle key not provided - trying to get one");
//         credentials.pickleKey =
//             (await PlatformPeg.get()?.getPickleKey(credentials.userId, credentials.deviceId)) ?? undefined;
//     }

//     return doSetLoggedIn(credentials, null, overwrite, false);
// }

/**
 * Transitions to a logged-in state using the given credentials.
 *
 * Starts the matrix client and all other react-sdk services that
 * listen for events while a session is logged in.
 *
 * Also clears old credentials/etc out of storage before starting the new client.
 *
 * @param {IMatrixClientCreds} credentials The credentials to use
 * @param {Boolean} clearStorageEnabled True to clear storage before starting the new client
 * @param {Boolean} isFreshLogin True if this is a fresh login, false if it is previous session being restored
 *
 * @returns {Promise} promise which resolves to the new MatrixClient once it has been started
 */
async function doSetLoggedIn(
    credentials: IMatrixClientCreds,
    authProvider: AUTH_PROVIDER_TYPE | null,
    clearStorageEnabled: boolean,
    isFreshLogin: boolean,
): Promise<MatrixClient> {
    // Il flag IsLoggedIn viene impostato a true, utente loggato
    setIsLoggedIn(true);

    try {
        checkSessionLock();
        credentials.guest = Boolean(credentials.guest);

        // Prelievo informazioni utente per poterle salvare insieme alle credenziali, 
        // e per poterle utilizzare subito dopo il login,
        // solo se l'utente loggato è uguale da quello salvato in precedenza
        let oldUserInfo: IDthUser | null = DthSessionStorage.getCurrentUserInfo();
        if (credentials.apiUserId !== oldUserInfo?.id) {
            // Se l'utente è diverso da quello salvato in precedenza, 
            // non ha senso mantenere le info utente salvate, 
            // che potrebbero essere relative ad un altro utente.
            oldUserInfo = null;
        }

        logger.debug("setLoggedIn: mxid: " +
            credentials.userId +
            " deviceId: " +
            credentials.deviceId +
            " guest: " +
            credentials.guest +
            " hs: " +
            credentials.homeserverUrl +
            " freshLogin: " + credentials.freshLogin,
        );

        if (clearStorageEnabled) {
            await clearStorage();
        }

        const results = await StorageManager.checkConsistency();
        // If there's an inconsistency between account data in local storage and the
        // crypto store, we'll be generally confused when handling encrypted data.
        // Show a modal recommending a full reset of storage.
        if (results.dataInLocalStorage && results.cryptoInited && !results.dataInCryptoStore) {
            logger.warn("doSetLoggedIn: StorageManager consistency check failed; displaying StorageEvictedDialog.");
            await abortLogin();
        }

        // Quando abbiamo effettuato l'autenticazione tramite flusso OIDC-native e abbiamo un token di refresh, 
        // proviamo a creare un token refresher. 
        // const tokenRefresher = await createOidcTokenRefresher(credentials);

        // check the session lock just before creating the new client
        checkSessionLock();
        // MatrixClientPeg.replaceUsingCreds(credentials, tokenRefresher?.doRefreshAccessToken.bind(tokenRefresher));
        MatrixClientPeg.replaceUsingCreds(credentials, refreshMatrixTokensWithMutex);

        setSentryUser(credentials.userId);

        // if (PosthogAnalytics.instance.isEnabled()) {
        //     PosthogAnalytics.instance.startListeningToSettingsChanges(client);
        // }

        if (!localStorage) {
            throw new Error("[Lifecycle] No local storage available: can't persist session!");
        }

        //#region Inizializzazione Memoria Utente
        credentials.pickleKey = await loadOrCreatePickleKey(credentials.userId, credentials.deviceId);

        //ATTENZIONE! Bisogna salvare prima le credenziali 
        // in modo da memorizzare l'accessToken che poi servirà per prelevare anche le info utente
        await DthSessionStorage.saveCredentials(credentials);
        // prelievo informazioni utente
        const dthApiClient = DthHttpClient.getInstance();
        const userInfo: IDthUser = await dthApiClient.getUserInfo({ apiUserId: credentials.apiUserId });
        const privateConfiguration: IDthPrivateConfiguration = await dthApiClient.getPrivateConfiguration();
        const refreshContacts: boolean = await ContactStateManager.refreshContactsFromServerAsync(userInfo.id);
        if (!refreshContacts) {
            logger.warn("Failed to refresh contacts from server for user:", userInfo.id);
        }

        // Salvataggio informazioni utente non reperibili con il solo access token
        // es. telemetryToken, repereribile solo al login e quindi si tenta di mantenerlo se già presente in local storage
        if (oldUserInfo != null && oldUserInfo.telemetryToken != null && !userInfo.telemetryToken) {
            logger.debug("Maintaining telemetry token from previous session", oldUserInfo.telemetryToken);
            userInfo.telemetryToken = oldUserInfo.telemetryToken;
        }

        DthSessionStorage.saveCurrentUserInfo(userInfo, authProvider);
        DthSessionStorage.savePrivateConfiguration(privateConfiguration);

        // Pulizia token di utenti diversi dal corrente, per evitare di avere token di utenti diversi in memoria che potrebbero essere usati per errore
        const currentUsername: string = await DthSessionStorage.tryGetUsername() || userInfo.email;
        // Cancellazione token di validazione di utenti diversi da quello corrente, per evitare di avere token di validazione di utenti diversi in memoria che potrebbero essere usati per errore
        await DthValidationTokenManager.onUserChanged(currentUsername);
        // Cancellare anche le recovery key (?)
        if (DELETE_RECOVERY_KEY_ON_ACCOUNT_CHANGE) {
            await DthRecoveryKeyManager.onUserChanged(currentUsername);
        }

        // request permission for push notifications
        const token = await requestFirebaseNotificationPermission();
        logger.debug("Firebase notification token:", token);

        // make sure we don't think that it's a fresh login any more
        sessionStorage.removeItem("mx_fresh_login");

        checkSessionLock();
        //#endregion

        //#region CHECK LICENSE
        try {
            const licenseJwt = await LicenseManager.validateAndStoreLoginLicenseJwt({
                telemetryToken: DthSessionStorage.getGenericItem(API_TELEMETRY_TOKEN_KEY) || "",
                userId: DthSessionStorage.getGenericItem(API_USER_ID_KEY) || ""
            });

            if (!LicenseManager.checkLicenseValidity(licenseJwt)) {
                throw new LicenseError(_t("error.license_error_message"));
            }
        } catch (e: any) {
            logger.error("Error during license validation:", e);
            // const errorMessage = LicenseManager.tryGetLicenseErrorMessage(e);
            // throw new LicenseError(errorMessage);

            // Mostra un messaggio specifico solo se l'errore riguarda la validazione della licenza.
            // Gli errori di rete vengono ignorati per evitare di bloccare l'accesso in caso di problemi temporanei di connettività.
            // Se siamo qui, la validazione della licenza è già stata eseguita all'avvio dell'app.
            if (e instanceof LicenseError) {
                throw new LicenseError(_t("error.license_error_message"));
            }
        }
        //#endregion

        // We are now logged in, so fire this. We have yet to start the client but the
        // client_started dispatch is for that.
        dis.fire(Action.OnLoggedIn);

        const clientPegOpts: MatrixClientPegAssignOpts = {};
        if (credentials.pickleKey) {
            // The pickleKey, if provided, is probably a base64-encoded 256-bit key, so can be used for the crypto store.
            if (credentials.pickleKey.length === 43) {
                clientPegOpts.rustCryptoStoreKey = decodeBase64(credentials.pickleKey);
            } else {
                // We have some legacy pickle key. Continue using it as a password.
                clientPegOpts.rustCryptoStorePassword = credentials.pickleKey;
            }
        }

        // Attendi 2 secondi prima di proseguire
        // Questo è un workaround per evitare che il safeGet() del MatrixClientPeg 
        // venga chiamato prima che il nuovo client sia assegnato, causando errori di "no client".
        await new Promise(resolve => setTimeout(resolve, 2000));

        // Viene creato un nuovo client, e assegnato a MatrixClientPeg, 
        // che è quello usato da tutta l'app. 
        // Quindi tutte le parti dell'app che usano MatrixClientPeg.safeGet() riceveranno questo nuovo client.
        const client = MatrixClientPeg.safeGet();

        try {
            await startMatrixClient(client, /*startSyncing=*/ true, clientPegOpts);
        } finally {
            clientPegOpts.rustCryptoStoreKey?.fill(0);
        }

        // Run the migrations after the MatrixClientPeg has been assigned
        SettingsStore.runMigrations(isFreshLogin);

        if (isFreshLogin && !credentials.guest) {
            // For newly registered users, set a flag so that we force them to verify,
            // (we don't want to force users with existing sessions to verify though)
            localStorage.setItem("must_verify_device", "true");
        }

        return client;
    } catch (e) {
        logger.error("Error in setLoggedIn", e);

        let title: string = _t("common.error");
        let description: string = _t("auth.login.generic_error");
        if (e instanceof LicenseError) {
            title = _t("error.license_error_title");
            description = _t("error.license_error_message");
        }

        Modal.createDialog(ErrorDialog, {
            title,
            description,
            button: _t("action.sign_out")
        },
            undefined,
            undefined,
            undefined,
            {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                onBeforeClose: async (reason): Promise<boolean> => {
                    try { logout(); } catch (err) {
                        logger.warn("Error during logout after failed login", err);
                    }
                    window.location.href = "#/login";
                    return Promise.resolve(true);
                }
            }
        );

        throw e;
    }
}

/**
 * Ritorna true se il MatrixClient è stoppato (sync fermo).
 */
export function isMatrixClientStopped(matrixClient: MatrixClient | null): boolean {
    // Se non c'è client o non è in esecuzione, consideriamo come stoppato
    return !matrixClient || !matrixClient.clientRunning;
}

//#region Token refresh

/**
 * Mutex per evitare chiamate concorrenti a refreshMatrixTokens, 
 * che potrebbero causare problemi di race condition e di sovrascrittura dei token salvati nello storage.
 * Viene usato per serializzare le chiamate a refreshMatrixTokens: se c'è già una chiamata in corso, 
 * le altre chiamate aspettano che la prima finisca e poi usano i token rinnovati salvati nello storage.
 * In questo modo si evita che più chiamate concorrenti a refreshMatrixTokens sovrascrivano i token salvati 
 * nello storage con token vecchi o inconsistenti, e si garantisce che tutte le chiamate ottengano token aggiornati e consistenti.
 */
let refreshMatrixTokensPromise: Promise<AccessTokens> | null = null;

/**
 * Rinnova access token e refresh token usando il refresh token, 
 * e aggiorna i token salvati nello storage
 * @param refreshToken Il refresh token da utilizzare
 * @param attemptDelayMs Il delay in ms da attendere tra un tentativo e l'altro in caso di errore
 * @param maxAttempts Il numero massimo di tentativi di refresh prima di forzare il logout
 * @returns Nuovi access/refresh token e expiry
 */
async function refreshMatrixTokens(
    refreshToken: string,
    attemptDelayMs: number = 1000,
    maxAttempts: number = 5): Promise<AccessTokens> {

    // Ultimo errore riscontrato durante i tentativi di refresh, per poterlo loggare alla fine se tutti i tentativi falliscono
    let lastError: any = null;

    // Stato client matrix: se è stoppato, 
    // non ha senso tentare di rinnovare i token, 
    // perché non riusciremo a sincronizzare comunque.
    let matrixClientStopped: boolean = false;

    // Ultimo refresh token effettivamente utilizzato per il tentativo di refresh,
    // inizialmente è quello passato come argomento, ma se durante i tentativi di refresh il token salvato nello storage cambia (es. per login in un'altra finestra), 
    // viene aggiornato con quello salvato, per evitare di continuare a tentare di rinnovare con un token ormai obsoleto.
    let lastRefreshToken: string = refreshToken;

    // Token salvato nello storage al momento dell'ultimo tentativo di refresh: 
    // viene usato per rilevare cambiamenti del token salvato durante i tentativi di refresh
    let savedRefreshToken: string | undefined = undefined;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
            console.debug(`[Lifecycle] Attempt ${attempt} to refresh tokens`);

            // Prima di ogni tentativo, preleva il refresh token salvato nello storage, 
            // per rilevare eventuali cambiamenti (es. login in un'altra finestra) e 
            // usarlo al posto di quello passato come argomento se è cambiato
            try {
                savedRefreshToken = await DthSessionStorage.getMatrixRefreshToken();
            } catch (e) {
                console.warn(`[Lifecycle] Attempt ${attempt}: Error retrieving refresh token from storage, used last known refresh token`, e);
            }

            // Se il refresh token fornito è diverso da quello salvato, logga un warning e usa quello salvato.
            if (savedRefreshToken && lastRefreshToken !== savedRefreshToken) {
                console.warn(`[Lifecycle] Attempt ${attempt}: Refresh token mismatch: provided refresh token does not match the one in storage. Using stored refresh token.`,
                    { provided: lastRefreshToken, stored: savedRefreshToken });
                lastRefreshToken = savedRefreshToken;
            }

            console.debug(`[Lifecycle] Attempt ${attempt}: Refreshing tokens at ${new Date().toISOString()} with refresh token: ${lastRefreshToken}`);

            // Viene prelevato il Matrix client
            const matrixClient: MatrixClient | null = MatrixClientPeg.get();
            // Se non c'è client, o è stoppato, esce dal loop di tentativi, per evitare di fare tentativi inutili
            if (!matrixClient || isMatrixClientStopped(matrixClient)) {
                matrixClientStopped = true;
                throw new CustomError("Matrix client is stopped or unavailable");
            }

            // Info necessarie per il refresh token
            const userId = matrixClient.getUserId();
            if (!userId) throw new CustomError("No user ID available on Matrix client to refresh token");
            const deviceId = matrixClient.getDeviceId();
            if (!deviceId) throw new CustomError("No device ID available on Matrix client to refresh token");
            const pickleKey = (await PlatformPeg.get()?.getPickleKey(userId, deviceId)) ?? undefined;
            const baseUrl = matrixClient.getHomeserverUrl();

            const response = await fetch(`${baseUrl}/_matrix/client/v3/refresh`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ refresh_token: lastRefreshToken }),
            });

            if (!response.ok) {
                throw new HttpResponseError(response.status, "Failed to refresh token", response);
            }

            const data = await response.json();
            console.debug(`[Lifecycle] Attempt ${attempt}: Token refresh response:`, data);

            // Protegge dal refresh loop: salva subito i nuovi token
            const newAccessToken = data.access_token;
            const newRefreshToken = data.refresh_token;
            const expiresInMs = data.expires_in_ms;
            const expiry = expiresInMs ? new Date(Date.now() + expiresInMs) : undefined

            console.debug(`[Lifecycle] Attempt ${attempt}: New token expiration:`, expiry?.toISOString() ?? "??");

            await DthSessionStorage.updateMatrixTokens(
                newAccessToken,
                newRefreshToken,
                pickleKey
            );

            return {
                accessToken: newAccessToken,
                refreshToken: newRefreshToken,
                expiry: expiry,
            };
        } catch (e: any) {
            lastError = e;

            if (matrixClientStopped) {
                console.warn(`[Lifecycle] Attempt ${attempt}: Matrix client is stopped or unavailable, cannot refresh token, aborting refresh attempts`, e);
                // esce dal loop di tentativi se il client è stoppato, 
                // per evitare di fare tentativi inutili
                break;
            }
            // Riconosce l'offline: se l'eccezione non è un HttpResponseError con status code, 
            // presumiamo che sia un errore di rete/offline
            if (!(e instanceof HttpResponseError) || !e.statusCode) {
                console.warn(`[Lifecycle] Attempt ${attempt}: Network error detected during token refresh, likely offline.`, e);
                // Non facciamo logout, lasciamo che il client riprovi
                throw new Error("Network offline, retrying when connection returns");
            }
            // Se errore non è offline, logga e riprova
            console.warn(`[Lifecycle] Attempt ${attempt} of ${maxAttempts} to refresh token failed:`, e);
            if (attempt < maxAttempts) {
                await new Promise(res => setTimeout(res, attemptDelayMs));
            }
        }
    }

    // Se il client è stoppato, non ha senso mostrare un errore di sessione scaduta,
    // perché la causa più probabile è che la sessione sia stata chiusa in un'altra finestra.
    // In questo caso, forziamo il logout senza mostrare un dialog di errore.
    if (matrixClientStopped) {
        logger.warn("Matrix client is stopped or not available, cannot refresh token", lastError);
        // await onLoggedOut();
        throw new Error("Matrix client is stopped, session ended");
    }

    logger.error("All attempts to refresh token failed", lastError);

    // Se l'utente è autenticato mostra il dialog per effettuare il logout altrimenti ignora
    if (IsLoggedIn()) {
        // Se siamo qui, tutti i tentativi sono falliti e non siamo offline: sessione scaduta
        // Feedback utente: mostra errore di sessione scaduta
        const modal = Modal.createDialog(ErrorDialog,
            {
                title: _t("auth.session_logged_out_title"),
                description: _t("auth.session_logged_out_description")
            },
            undefined,
            undefined,
            undefined,
            {
                onBeforeClose: async (_): Promise<boolean> => {
                    logger.warn("refreshMatrixTokens(): Session expired, logging out");
                    // Chiamo direttamente onLoggedOut invece di logout,
                    // perché non riuscendo a ottenere il client Matrix non ha senso tentare il logout lato server.
                    // È sufficiente pulire lo stato dell'app e tornare alla schermata di login.
                    await onLoggedOut();
                    return Promise.resolve(true);
                }
            }
        );
        // Aspetta che l'utente chiuda il dialog, poi forza logout. 
        // Non facciamo logout immediato per dare all'utente la possibilità di leggere l'errore, 
        // e per evitare di chiudere improvvisamente l'app senza spiegazione.
        await modal.finished;
    } else {
        logger.warn("refreshMatrixTokens(): Not logged in, cannot refresh token, ignoring logout", lastError);
    }

    throw lastError || new Error("Failed to refresh token");
}

/**
 * Rinnova access token e refresh token usando il refresh token, con gestione di concorrenza e errori.
 * @param refreshToken Il refresh token da utilizzare per il rinnovo dei token
 * @param attemptDelayMs Il delay in ms da attendere tra un tentativo e l'altro in caso di errore
 * @param maxAttempts Il numero massimo di tentativi di refresh prima di forzare il logout
 * @returns Nuovi access/refresh token e expiry
 */
async function refreshMatrixTokensWithMutex(
    refreshToken: string,
    attemptDelayMs: number = 1000,
    maxAttempts: number = 5
): Promise<AccessTokens> {
    logger.debug("refreshMatrixTokens called with refresh token:", refreshToken);
    if (refreshMatrixTokensPromise) {
        logger.debug("refreshMatrixTokens: Refresh already in progress, returning existing promise");
        return refreshMatrixTokensPromise;
    }

    try {
        // Se non c'è già un tentativo in corso, ne avvia uno nuovo e salva la promise nel mutex
        refreshMatrixTokensPromise = refreshMatrixTokens(refreshToken, attemptDelayMs, maxAttempts);
        const result = await refreshMatrixTokensPromise;
        return result;
    } finally {
        logger.debug("refreshMatrixTokens: Refresh attempt finished, resetting mutex");
        // Reset solo se la promise corrente è ancora la stessa (evita race condition)
        refreshMatrixTokensPromise = null;
    }

}
//#endregion

// Oidc viene gestito diversamente tramite il server DTH 
// /**
//  * When we have a authenticated via OIDC-native flow and have a refresh token
//  * try to create a token refresher.
//  * @param credentials from current session
//  * @returns Promise that resolves to a TokenRefresher, or undefined
//  */
// async function createOidcTokenRefresher(credentials: IMatrixClientCreds): Promise<OidcTokenRefresher | undefined> {
//     if (!credentials.refreshToken) {
//         return;
//     }
//     // stored token issuer indicates we authenticated via OIDC-native flow
//     const tokenIssuer = getStoredOidcTokenIssuer();
//     if (!tokenIssuer) {
//         return;
//     }
//     try {
//         const clientId = getStoredOidcClientId();
//         const idTokenClaims = getStoredOidcIdTokenClaims();
//         const redirectUri = PlatformPeg.get()!.getOidcCallbackUrl().href;
//         const deviceId = credentials.deviceId;
//         if (!deviceId) {
//             throw new Error("Expected deviceId in user credentials.");
//         }
//         const tokenRefresher = new TokenRefresher(
//             tokenIssuer,
//             clientId,
//             redirectUri,
//             deviceId,
//             idTokenClaims!,
//             credentials.userId,
//         );
//         // wait for the OIDC client to initialise
//         await tokenRefresher.oidcClientReady;
//         return tokenRefresher;
//     } catch (error) {
//         console.error("[Lifecycle] Failed to initialise OIDC token refresher", error);
//     }
// }

async function showStorageEvictedDialog(): Promise<boolean> {
    const { finished } = Modal.createDialog(StorageEvictedDialog);
    const [ok] = await finished;
    return !!ok;
}

// Note: Babel 6 requires the `transform-builtin-extend` plugin for this to satisfy
// `instanceof`. Babel 7 supports this natively in their class handling.
class AbortLoginAndRebuildStorage extends Error { }

/**
 * Stato utente se loggato
 */
let _isLoggedIn = false;
// /**
//  * Stato di logout in corso
//  */
// let _isLoggingOut = false;

export function IsLoggedIn(): boolean {
    return _isLoggedIn;
}
function setIsLoggedIn(val: boolean): void {
    _isLoggedIn = val;
    logger.debug("Set _isLoggedIn to ", val);
}

// export function IsLoggingOut(): boolean {
//     return _isLoggingOut;
// }
// function setIsLoggingOut(val: boolean): void {
//     _isLoggingOut = val;
//     console.debug("[Lifecycle] Set _isLoggingOut to ", val);
// }

/**
 * Logs out the current session.
 * When user has authenticated using OIDC native flow revoke tokens with OIDC provider.
 * Otherwise, call /logout on the homeserver.
 * @param client
 * @ param oidcClientStore
 */
async function doLogout(
    apiClient: DthHttpClient,
    matrixClient: MatrixClient,
    // oidcClientStore?: OidcClientStore
): Promise<void> {
    logger.debug("Performing doLogout");

    // if (oidcClientStore?.isUserAuthenticatedWithOidc) {
    //     const accessToken = matrixClient.getAccessToken() ?? undefined;
    //     const refreshToken = matrixClient.getRefreshToken() ?? undefined;

    //     await oidcClientStore.revokeTokens(accessToken, refreshToken);
    // } else {
    try {
        logger.debug("Logging out from api server");
        // logout lato api server per invalidare il token, 
        // ma non blocchiamo il logout se fallisce, 
        // perché vogliamo comunque pulire lo stato locale e tornare alla schermata di login
        await apiClient.logout({ deviceId: matrixClient.getDeviceId() || "" });
    } catch (err) {
        logger.warn("Failed to call logout API, proceeding with local logout", err);
    }

    try {
        logger.debug("Logging out from matrix server");
        // logout lato matrix server per invalidare il token,
        // ma non blocchiamo il logout se fallisce,
        // perché vogliamo comunque pulire lo stato locale e tornare alla schermata di login
        // true serve per fermare il client prima di effettuare il logout, 
        // in modo da evitare problemi di token scaduto durante il logout
        await matrixClient.logout(true);
    } catch (err) {
        logger.warn("Failed to call logout API, proceeding with local logout", err);
    }
    // }
}

/**
 * Logs the current session out and transitions to the logged-out state
 * @ param oidcClientStore store instance from SDKContext
 */
export function logout(
    // oidcClientStore?: OidcClientStore
): void {
    logger.debug("Performing logout");

    // if (IsLoggingOut()) {
    //     console.warn("[Lifecycle] Logout already in progress, ignoring additional call");
    //     return;
    // }

    if (!IsLoggedIn()) {
        logger.warn("Logout called but user is not logged in, ignoring.");
        return;
    }

    const matrixClient: MatrixClient | null = MatrixClientPeg.get();
    if (!matrixClient) {
        logger.warn("No Matrix client found during logout, ignoring logout request.");
        return;
    }

    const dthApiClient: DthHttpClient = DthHttpClient.getInstance();

    // PosthogAnalytics.instance.logout();

    if (matrixClient.isGuest()) {
        // logout doesn't work for guest sessions
        // Also we sometimes want to re-log in a guest session if we abort the login.
        // defer until next tick because it calls a synchronous dispatch, and we are likely here from a dispatch.
        setTimeout(onLoggedOut, 0);
        return;
    }

    // Imposta lo stato di non loggato subito, per evitare che altre parti dell'app
    // anche dopo l'esecuzione di questo metodo pensino che siamo ancora loggati,
    // evitando di lanciare nuovamente il logout
    setIsLoggedIn(false);

    // onLoggedOut viene chiamato sia se il logout lato server ha successo, sia se fallisce, 
    // perché in entrambi i casi vogliamo comunque pulire lo stato locale e tornare alla schermata di login
    // viene forzato il force=true, perchè bisogna forzare il logout se arriviamo qui
    doLogout(
        dthApiClient,
        matrixClient,
        // oidcClientStore
    ).then(
        (_) => onLoggedOut(true),
        (err) => {
            // Just throwing an error here is going to be very unhelpful
            // if you're trying to log out because your server's down and
            // you want to log into a different server, so just forget the
            // access token. It's annoying that this will leave the access
            // token still valid, but we should fix this by having access
            // tokens expire (and if you really think you've been compromised,
            // change your password).
            logger.warn("Failed to call logout API: token will not be invalidated", err);
            onLoggedOut(true);
        });
}

/**
 * Starts the matrix client and all other react-sdk services that
 * listen for events while a session is logged in.
 *
 * @param client the matrix client to start
 * @param startSyncing - `true` to actually start syncing the client.
 * @param clientPegOpts - Options to pass through to {@link MatrixClientPeg.start}.
 */
async function startMatrixClient(
    client: MatrixClient,
    startSyncing: boolean,
    clientPegOpts: MatrixClientPegAssignOpts,
): Promise<void> {
    logger.debug("Starting MatrixClient");

    // dispatch this before starting the matrix client: it's used
    // to add listeners for the 'sync' event so otherwise we'd have
    // a race condition (and we need to dispatch synchronously for this
    // to work).
    dis.dispatch({ action: "will_start_client" }, true);

    // reset things first just in case
    SdkContextClass.instance.typingStore.reset();
    ToastStore.sharedInstance().reset();

    DialogOpener.instance.prepare(client);
    Notifier.start();
    UserActivity.sharedInstance().start();
    DMRoomMap.makeShared(client).start();
    IntegrationManagers.sharedInstance().startWatching();
    ActiveWidgetStore.instance.start();
    // LegacyCallHandler.instance.start();
    checkBrowserSupport();

    // Start Mjolnir even though we haven't checked the feature flag yet. Starting
    // the thing just wastes CPU cycles, but should result in no actual functionality
    // being exposed to the user.
    // Mjolnir.sharedInstance().start();

    if (startSyncing) {
        // The client might want to populate some views with events from the
        // index (e.g. the FilePanel), therefore initialize the event index
        // before the client.
        await EventIndexPeg.init();
        await MatrixClientPeg.start(clientPegOpts);
    } else {
        logger.warn("Caller requested only auxiliary services be started");
        await MatrixClientPeg.assign(clientPegOpts);
    }

    checkSessionLock();

    // This needs to be started after crypto is set up
    DeviceListener.sharedInstance().start(client);
    // Similarly, don't start sending presence updates until we've started
    // the client
    if (!SettingsStore.getValue("lowBandwidth")) {
        Presence.start();
    }

    // Now that we have a MatrixClientPeg, update the Jitsi info
    // Jitsi.getInstance().start();

    // dispatch that we finished starting up to wire up any other bits
    // of the matrix client that cannot be set prior to starting up.
    dis.dispatch({ action: "client_started" });
}

/**
 * Stops a running client and all related services, and clears persistent storage. Used after a session has been logged out.
 * @param force - If true, forces the logout process even if the user is not logged in. 
 * This is useful in cases where we want to ensure that all logout procedures are executed, 
 * such as when we encounter an error during logout or when we want to clean up the state regardless of the login status.
 */
export async function onLoggedOut(force?: boolean): Promise<void> {
    logger.debug("Performing onLoggedOut with force=", force, "IsLoggedIn=", IsLoggedIn());

    // Se non siamo loggati, non ha senso fare logout, quindi logghiamo un warning e ignoriamo la chiamata.
    // A meno che il force non sia true, in quel caso forziamo il logout anche se non siamo loggati
    if (!force && !IsLoggedIn()) {
        logger.warn("onLoggedOut called but user is not logged in, ignoring.");
        return;
    }

    // // Se onLoggedOut è già in corso, logga un warning e ignora la chiamata aggiuntiva.
    // if (IsLoggingOut()) {
    //     console.warn("[Lifecycle] onLoggedOut already in progress, ignoring additional call");
    //     return;
    // }

    // // Imposta il flag di logout in corso prima di chiamare stopMatrixClient e clearStorage, 
    // // per evitare che eventuali errori o callback multipli da queste funzioni 
    // // causino più chiamate a onLoggedOut o interferiscano tra loro.
    // setIsLoggingOut(true);
    // Imposta lo stato di non loggato subito, per evitare che altre parti dell'app
    // anche dopo l'esecuzione di questo metodo pensino che siamo ancora loggati,
    // evitando di lanciare nuovamente il logout
    setIsLoggedIn(false);

    try {
        // Assicura che il dispatch avvenga prima di fermare il client, 
        // in modo che i componenti React si smontino prima, 
        // evitando errori di "no client" quando cercano di accedere al client 
        // dopo che è stato fermato.
        dis.fire(Action.OnLoggedOut, true);

        // Chiude tutti i modali aperti, in modo da evitare 
        // che restino modali orfani dopo il logout 
        dis.dispatch({ action: "logout" });

        try { stopMatrixClient(); } catch (err) { logger.warn("Error stopping Matrix client during logout", err); }
        try { ContactStateManager.reset(); } catch (err) { logger.warn("Error resetting contact state manager during logout", err); }
        try { useCallStore.getState().resetCallState(); } catch (err) { logger.warn("Error resetting call store during logout", err); }
        try { await clearStorage(); } catch (err) { logger.warn("Error clearing storage during logout", err); }
        // LifecycleCustomisations.onLoggedOutAndStorageCleared?.();
        try { await PlatformPeg.get()?.clearStorage(); } catch (err) { logger.warn("Error clearing platform storage during logout", err); }
        try { SettingsStore.reset(); } catch (err) { logger.warn("Error resetting settings store during logout", err); }
        try { DthLoginPasswordCache.clear(); } catch (err) { logger.warn("Error clearing login password cache during logout", err); }

        // Do this last, so we can make sure all storage has been cleared and all
        // customisations got the memo.
        if (SdkConfig.get().logout_redirect_url) {
            logger.debug("Redirecting to external provider to finish logout");
            // XXX: Defer this so that it doesn't race with MatrixChat unmounting the world by going to /#/login
            window.setTimeout(() => {
                window.location.href = SdkConfig.get().logout_redirect_url!;
            }, 100);
        }
    } catch (err) {
        logger.error("Error during onLoggedOut", err);
    } finally {
        // // Do this last to prevent racing `stopMatrixClient` and `on_logged_out` with MatrixChat handling Session.logged_out
        // setIsLoggingOut(false);
    }
}

/**
 * @returns {Promise} promise which resolves once the stores have been cleared
 */
export async function clearStorage(): Promise<void> {
    logger.debug("Clearing storage");

    // Clear custom session and local storage (includes IndexedDB)
    try {
        await DthSessionStorage.clearStorage();
    } catch (err) {
        logger.warn("Error clearing DthSessionStorage", err);
    }
    logger.debug("DthSessionStorage cleared");

    // Delete event index (custom event store)
    try {
        await EventIndexPeg.deleteEventIndex();
    } catch (err) {
        logger.warn("Error deleting EventIndex", err);
    }
    logger.debug("EventIndexPeg cleared");

    // Clear matrix-js-sdk persistent stores (must be last to avoid IndexedDB deadlocks)
    try {
        const cli = createMatrixClient({
            // we'll never make any requests, so can pass a bogus HS URL
            baseUrl: "",
        });
        await promiseWithTimeout(
            cli.clearStores(),
            20000,
            "clearStores timeout"
        );
    } catch (err) {
        if (err instanceof TimeoutError) {
            console.warn("[MatrixChat] clearStores timeout: Redirecting to login page after session logged out:", err);

            const modal = Modal.createDialog(ErrorDialog, {
                title: _t("common.error"),
                description: _t("settings.general.error_generic"),
            });
            // Aspetta che l'utente chiuda il dialog, poi forza redirect alla pagina di login.
            await modal.finished;

            // DEVO FORZARE IL RELOAD PER CHIUDERE RICARICARE IL MATRIX CLIENT
            // A CAUSA DI UN BUG DELLA LIBRERIA MATRIX
            window.location.reload();
            return;
        }
        logger.warn("Error clearing matrix-js-sdk stores", err);
    }
    logger.debug("matrix-js-sdk stores cleared");
}

/**
 * Stop all the background processes related to the current client.
 * @param {boolean} unsetClient True (default) to abandon the client
 * on MatrixClientPeg after stopping.
 */
export function stopMatrixClient(unsetClient = true): void {
    try {
        Notifier.stop();
        // LegacyCallHandler.instance.stop();
        UserActivity.sharedInstance().stop();
        SdkContextClass.instance.typingStore.reset();
        Presence.stop();
        ActiveWidgetStore.instance.stop();
        IntegrationManagers.sharedInstance().stopWatching();
        // Mjolnir.sharedInstance().stop();
        DeviceListener.sharedInstance().stop();
        DMRoomMap.shared()?.stop();
        EventIndexPeg.stop();
        const cli = MatrixClientPeg.get();
        if (cli) {
            cli.stopClient();
            cli.removeAllListeners();

            if (unsetClient) {
                MatrixClientPeg.unset();
                EventIndexPeg.unset();
                cli.store.destroy();
            }
        }
    }
    catch (err) {
        logger.error("Error stopping Matrix client", err);
    }
}